import React, { useEffect, useState } from 'react';
import { 
  Check, 
  Sparkles, 
  ShieldCheck, 
  FileText, 
  Download, 
  Search, 
  Zap, 
  ArrowRight,
  PhoneCall,
  Crown,
  HelpCircle,
  Users,
  BrainCircuit,
  Bookmark,
  Building2,
  FileSpreadsheet,
  X
} from 'lucide-react';
import apiClient from '../../Api/ApiClient';
import { useAuthModal } from '../../contexts/AuthModalContext';

import { formulaService, UserFormulaInfo } from '../../Services/FormulaService';

// Les 13 fonctionnalités exhaustives de la grille officielle Docarn Juridique
interface PlanFeature {
  id: string;
  name: string;
  discovery: { included: boolean; label?: string };
  initiale: { included: boolean; label?: string };
  essentiel: { included: boolean; label?: string };
  professionnel: { included: boolean; label?: string };
}

export const FormulasView: React.FC = () => {
  const { openAuthModal } = useAuthModal();
  const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
  const [userFormula, setUserFormula] = useState<UserFormulaInfo | null>(null);
  const [discoveryQuota, setDiscoveryQuota] = useState<number>(5);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [displayMode, setDisplayMode] = useState<'cards' | 'table'>('cards');

  // Charger le quota configuré pour la formule Découverte
  useEffect(() => {
    formulaService.getDiscoveryQuota().then(info => {
      if (info && typeof info.maxConsultations === 'number' && info.maxConsultations > 0) {
        setDiscoveryQuota(info.maxConsultations);
      }
    });
  }, []);

  useEffect(() => {
    if (!token) {
      setIsLoading(false);
      return;
    }

    formulaService.getMyFormula()
      .then((res) => {
        if (res) {
          setUserFormula(res);
          const isDisc = res.groupName?.toLowerCase().includes('découverte') || res.groupName?.toLowerCase().includes('decouverte');
          if (isDisc && res.maxConsultations > 0) {
            setDiscoveryQuota(res.maxConsultations);
          }
        }
      })
      .catch((err) => {
        console.warn("Impossible de charger la formule actuelle de l'utilisateur", err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [token]);

  // Liste dynamique des fonctionnalités selon le quota Découverte configuré en base
  const featuresList: PlanFeature[] = React.useMemo(() => [
    {
      id: 'free_search',
      name: 'Recherche par expression libre',
      discovery: { included: true },
      initiale: { included: true },
      essentiel: { included: true },
      professionnel: { included: true }
    },
    {
      id: 'status_search',
      name: 'Recherche par statut juridique',
      discovery: { included: true },
      initiale: { included: true },
      essentiel: { included: true },
      professionnel: { included: true }
    },
    {
      id: 'domains',
      name: '12 domaines thématiques',
      discovery: { included: true },
      initiale: { included: true },
      essentiel: { included: true },
      professionnel: { included: true }
    },
    {
      id: 'regional_texts',
      name: 'Textes OHADA, CEMAC, COBAC',
      discovery: { included: true },
      initiale: { included: true },
      essentiel: { included: true },
      professionnel: { included: true }
    },
    {
      id: 'official_journals',
      name: 'Journaux officiels numérisés',
      discovery: { included: true },
      initiale: { included: true },
      essentiel: { included: true },
      professionnel: { included: true }
    },
    {
      id: 'consultation',
      name: 'Consultation des documents',
      discovery: { included: true, label: `${discoveryQuota} Acte${discoveryQuota > 1 ? 's' : ''} offert${discoveryQuota > 1 ? 's' : ''}` },
      initiale: { included: true, label: 'Illimitée (6 mois)' },
      essentiel: { included: true, label: 'Illimitée (12 mois)' },
      professionnel: { included: true, label: 'Illimitée (12 mois)' }
    },
    {
      id: 'notes',
      name: 'Notes personnalisées',
      discovery: { included: false },
      initiale: { included: false },
      essentiel: { included: true },
      professionnel: { included: true }
    },
    {
      id: 'downloads',
      name: 'Téléchargement des documents',
      discovery: { included: false, label: '0 Téléchargement' },
      initiale: { included: false, label: '0 Téléchargement' },
      essentiel: { included: true, label: '30 Téléchargements' },
      professionnel: { included: true, label: '30 Téléchargements' }
    },
    {
      id: 'favorites',
      name: 'Espace personnel et gestion des favoris',
      discovery: { included: false },
      initiale: { included: false },
      essentiel: { included: false },
      professionnel: { included: false }
    },
    {
      id: 'search_help',
      name: 'Aide pour la recherche d’un document ou d’un texte',
      discovery: { included: false },
      initiale: { included: false },
      essentiel: { included: false },
      professionnel: { included: false }
    },
    {
      id: 'multi_users',
      name: 'Accès multi-utilisateurs / comptes équipe',
      discovery: { included: false },
      initiale: { included: false },
      essentiel: { included: false },
      professionnel: { included: false }
    },
    {
      id: 'dedicated_support',
      name: 'Accompagnement documentaire dédié',
      discovery: { included: false },
      initiale: { included: false },
      essentiel: { included: false },
      professionnel: { included: false }
    },
    {
      id: 'ai_analysis',
      name: 'Analyse des documents par l’IA et recherches contextuelle IA (Fonctionnalité à venir)',
      discovery: { included: false },
      initiale: { included: false },
      essentiel: { included: false },
      professionnel: { included: true, label: 'Inclus (À venir)' }
    }
  ], [discoveryQuota]);

  // Catalogues des 4 formules Docarn Juridique :
  // 1. Découverte (La formule gratuite configurée dans l'application avec 5 actes offerts)
  // 2. Initiale (Nouvelle appellation de la formule "Découverte – 6 Mois" à 70 000 FCFA de la fiche)
  // 3. Essentiel (12 Mois – 110 000 FCFA)
  // 4. Professionnel (12 Mois – 200 000 FCFA)
  const formulasCatalog = [
    {
      id: 'discovery',
      name: 'Découverte',
      badge: 'Accès Gratuit Inscription',
      themeColor: 'border-slate-300',
      headerBg: 'bg-slate-800',
      isCurrent: userFormula?.groupName?.toLowerCase().includes('découverte') || userFormula?.groupName?.toLowerCase().includes('decouverte'),
      price: '0 FCFA',
      period: 'Gratuit à l\'inscription',
      duration: 'Offre d\'accueil',
      description: 'Formule activée automatiquement à la création de votre compte pour découvrir la plateforme.',
      highlight: false,
      ctaLabel: token ? 'Formule active' : 'Créer un compte gratuit',
      ctaAction: () => {
        if (!token) window.location.href = '/register';
      },
      keyFeature: 'discovery' as const
    },
    {
      id: 'initiale',
      name: 'Initiale',
      badge: 'Semestriel (6 Mois)',
      themeColor: 'border-emerald-600',
      headerBg: 'bg-[#2f5c2f]',
      isCurrent: userFormula?.groupName?.toLowerCase().includes('initiale') || userFormula?.groupName?.toLowerCase().includes('standard'),
      price: '70 000 FCFA',
      period: 'Pour 6 mois d\'accès',
      duration: '6 Mois',
      description: 'L\'accès semestriel complet pour consulter tous les textes officiels sans restriction de quota.',
      highlight: false,
      ctaLabel: 'Souscrire l\'offre Initiale',
      ctaAction: () => {
        window.location.href = 'mailto:contact@docarnservices.com?subject=Souscription Formule Initiale 6 Mois (70 000 FCFA)&body=Bonjour,%20je%20souhaite%20souscrire%20à%20la%20formule%20Initiale%206%20mois%20pour%20mon%20compte%20Docarn%20Juridique.';
      },
      keyFeature: 'initiale' as const
    },
    {
      id: 'essentiel',
      name: 'Essentiel',
      badge: 'Le Plus Populaire',
      themeColor: 'border-amber-600',
      headerBg: 'bg-[#c25e00]',
      isCurrent: userFormula?.groupName?.toLowerCase().includes('essentiel'),
      price: '110 000 FCFA',
      period: 'Pour 12 mois d\'accès',
      duration: '12 Mois',
      description: 'La formule de référence des juristes et praticiens avec notes privées et 30 téléchargements PDF.',
      highlight: true,
      ctaLabel: 'Souscrire l\'offre Essentiel',
      ctaAction: () => {
        window.location.href = 'mailto:contact@docarnservices.com?subject=Souscription Formule Essentiel 12 Mois (110 000 FCFA)&body=Bonjour,%20je%20souhaite%20souscrire%20à%20la%20formule%20Essentiel%2012%20mois%20pour%20mon%20compte%20Docarn%20Juridique.';
      },
      keyFeature: 'essentiel' as const
    },
    {
      id: 'professionnel',
      name: 'Professionnel',
      badge: 'Excellence & IA',
      themeColor: 'border-slate-800',
      headerBg: 'bg-[#26374a]',
      isCurrent: userFormula?.groupName?.toLowerCase().includes('professionnel') || userFormula?.groupName?.toLowerCase().includes('pro'),
      price: '200 000 FCFA',
      period: 'Pour 12 mois d\'accès',
      duration: '12 Mois',
      description: 'Pour les experts exigeants : inclut l\'analyse documentaire par Intelligence Artificielle et recherches contextuelles.',
      highlight: false,
      ctaLabel: 'Souscrire l\'offre Professionnel',
      ctaAction: () => {
        window.location.href = 'mailto:contact@docarnservices.com?subject=Souscription Formule Professionnel 12 Mois (200 000 FCFA)&body=Bonjour,%20je%20souhaite%20souscrire%20à%20la%20formule%20Professionnel%2012%20mois%20pour%20mon%20compte%20Docarn%20Juridique.';
      },
      keyFeature: 'professionnel' as const
    }
  ];

  return (
    <div className="py-8 sm:py-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
      {/* Header & Titre */}
      <div className="text-center max-w-3xl mx-auto mb-10">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-violet-100 text-violet-800 text-xs font-bold uppercase tracking-wider mb-4 shadow-sm">
          <Sparkles className="w-3.5 h-3.5 text-amber-500" />
          DOCARN JURIDIQUE • OFFRES D'ABONNEMENT PARTICULIER
        </div>
        <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight leading-tight">
          Consultez et exploitez le droit camerounais en toute sérénité
        </h1>
        <p className="mt-3 text-sm sm:text-base text-slate-600 leading-relaxed">
          Commencez avec la formule <strong className="text-slate-800">Découverte</strong> gratuite ({discoveryQuota} consultation{discoveryQuota > 1 ? 's' : ''} offerte{discoveryQuota > 1 ? 's' : ''}) ou accédez à nos formules complètes semestrielles et annuelles.
        </p>

        {/* Boutons de bascule d'affichage : Cartes / Tableau comparatif */}
        <div className="mt-6 inline-flex p-1 bg-slate-100 rounded-2xl border border-slate-200">
          <button
            type="button"
            onClick={() => setDisplayMode('cards')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
              displayMode === 'cards'
                ? 'bg-white text-violet-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Vue Cartes Détaillées
          </button>
          <button
            type="button"
            onClick={() => setDisplayMode('table')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
              displayMode === 'table'
                ? 'bg-white text-violet-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Grille Officielle Comparative
          </button>
        </div>
      </div>

      {/* Bannière utilisateur connecté */}
      {token && userFormula && (
        <div className="mb-10 bg-gradient-to-r from-slate-900 via-indigo-950 to-violet-950 rounded-3xl p-6 sm:p-8 text-white shadow-xl border border-indigo-900/50 relative overflow-hidden">
          <div className="absolute top-0 right-0 -mr-12 -mt-12 w-48 h-48 bg-violet-600/20 rounded-full blur-2xl pointer-events-none"></div>
          
          <div className="relative z-10 grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
            <div className="md:col-span-7 space-y-2">
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-amber-400">
                <Crown className="w-4 h-4" />
                Votre Statut d'Abonné Actuel
              </div>
              <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                {userFormula.groupName}
                <span className="text-xs px-2.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-400/30">
                  Actif
                </span>
              </h2>
              <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
                {userFormula.groupDescription || "Formule associée à votre compte pour la consultation et le téléchargement."}
              </p>
            </div>

            <div className="md:col-span-5 bg-white/10 backdrop-blur-md rounded-2xl p-5 border border-white/15 space-y-3">
              <div>
                <div className="flex justify-between items-center text-xs font-semibold mb-1.5">
                  <span className="text-slate-200">Actes consultés</span>
                  <span className="font-mono font-bold text-amber-300">
                    {userFormula.isUnlimitedConsultations ? (
                      'Consultations Illimitées'
                    ) : (
                      `${userFormula.consultationsUsed} / ${userFormula.maxConsultations} actes`
                    )}
                  </span>
                </div>
                {!userFormula.isUnlimitedConsultations && (
                  <div className="w-full bg-white/20 h-2.5 rounded-full overflow-hidden">
                    <div 
                      className={`h-full transition-all duration-500 rounded-full ${
                        (userFormula.consultationsUsed / Math.max(userFormula.maxConsultations, 1)) >= 1
                          ? 'bg-red-400'
                          : (userFormula.consultationsUsed / Math.max(userFormula.maxConsultations, 1)) >= 0.7
                            ? 'bg-amber-400'
                            : 'bg-emerald-400'
                      }`}
                      style={{ 
                        width: `${Math.min(100, Math.round((userFormula.consultationsUsed / Math.max(userFormula.maxConsultations, 1)) * 100))}%` 
                      }}
                    ></div>
                  </div>
                )}
              </div>

              <div className="pt-2 border-t border-white/10 flex items-center justify-between text-xs text-slate-300">
                <span>Téléchargements quotidiens :</span>
                <span className="font-bold text-white font-mono">
                  {userFormula.maxDailyDownloads < 0 ? 'Illimité' : `${userFormula.maxDailyDownloads} / jour`}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* VUE 1 : CARTES DES FORMULES AVEC TOUTES LES FONCTIONNALITÉS (INCLUSES & BARRÉES) */}
      {displayMode === 'cards' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch mb-16">
          {formulasCatalog.map((formula) => {
            const isHighlighted = formula.highlight;

            return (
              <div
                key={formula.id}
                className={`rounded-3xl flex flex-col justify-between transition-all duration-300 relative bg-white border ${
                  isHighlighted
                    ? 'border-2 border-amber-600 shadow-2xl shadow-amber-600/10 lg:-translate-y-2'
                    : 'border-slate-200 shadow-lg hover:shadow-xl'
                }`}
              >
                {/* Badge supérieur */}
                {formula.badge && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 z-10">
                    <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider shadow-sm ${
                      isHighlighted
                        ? 'bg-amber-600 text-white'
                        : formula.id === 'initiale'
                          ? 'bg-emerald-700 text-white'
                          : formula.id === 'professionnel'
                            ? 'bg-slate-800 text-white'
                            : 'bg-slate-200 text-slate-700'
                    }`}>
                      {formula.badge}
                    </span>
                  </div>
                )}

                {/* En-tête de carte coloré selon la fiche */}
                <div className={`p-6 rounded-t-3xl text-white ${formula.headerBg}`}>
                  <h3 className="text-xl font-bold tracking-tight">{formula.name}</h3>
                  <div className="mt-3 flex items-baseline gap-1">
                    <span className="text-2xl sm:text-3xl font-extrabold tracking-tight">{formula.price}</span>
                  </div>
                  <span className="text-xs text-white/80 block mt-0.5">{formula.period}</span>
                </div>

                {/* Corps de la carte avec toutes les fonctionnalités */}
                <div className="p-6 flex-1 flex flex-col justify-between">
                  <div>
                    <p className="text-xs text-slate-500 mb-5 min-h-[40px] leading-relaxed">
                      {formula.description}
                    </p>

                    <div className="space-y-2.5 mb-6">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 pb-1 border-b border-slate-100">
                        Détail des 13 fonctionnalités :
                      </p>
                      {featuresList.map((feat) => {
                        const planStatus = feat[formula.keyFeature];
                        const isIncluded = planStatus.included;
                        const customLabel = planStatus.label;

                        return (
                          <div 
                            key={feat.id} 
                            className={`flex items-start gap-2.5 text-xs py-1 transition-colors ${
                              isIncluded ? 'text-slate-800' : 'text-slate-400'
                            }`}
                          >
                            {isIncluded ? (
                              <div className="w-4 h-4 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0 mt-0.5">
                                <Check className="w-3 h-3 stroke-[2.5]" />
                              </div>
                            ) : (
                              <div className="w-4 h-4 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center shrink-0 mt-0.5">
                                <span className="text-[11px] font-bold leading-none">—</span>
                              </div>
                            )}

                            <span className={`leading-snug ${isIncluded ? 'font-medium' : 'line-through text-slate-400 opacity-60'}`}>
                              {feat.name}
                              {customLabel && (
                                <strong className={`block text-[11px] ${isIncluded ? 'text-violet-700 font-bold' : 'text-slate-400 font-normal no-underline'}`}>
                                  ({customLabel})
                                </strong>
                              )}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Bouton d'action */}
                  <div className="pt-4 border-t border-slate-100">
                    <button
                      type="button"
                      onClick={formula.ctaAction}
                      disabled={formula.isCurrent}
                      className={`w-full py-3 px-4 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 cursor-pointer ${
                        formula.isCurrent
                          ? 'bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200'
                          : isHighlighted
                            ? 'bg-amber-600 hover:bg-amber-700 text-white shadow-md shadow-amber-600/20 hover:-translate-y-0.5'
                            : formula.id === 'initiale'
                              ? 'bg-emerald-700 hover:bg-emerald-800 text-white shadow-md shadow-emerald-700/20 hover:-translate-y-0.5'
                              : 'bg-slate-900 hover:bg-slate-800 text-white hover:-translate-y-0.5'
                      }`}
                    >
                      <span>{formula.isCurrent ? 'Votre formule active' : formula.ctaLabel}</span>
                      {!formula.isCurrent && <ArrowRight className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* VUE 2 : GRILLE COMPARATIVE OFFICIELLE TYPE TABLEAU (CONFORME À LA FICHE DOCARN) */}
      <div className={`mb-16 ${displayMode === 'table' ? 'block' : 'mt-12'}`}>
        <div className="bg-white rounded-3xl shadow-xl border border-slate-200 overflow-hidden">
          {/* Entête du tableau */}
          <div className="p-6 bg-slate-900 text-white flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <div className="text-amber-400 text-xs font-bold uppercase tracking-wider">
                Fiche Tarifaire Officielle
              </div>
              <h2 className="text-xl font-bold text-white mt-1">
                Comparatif détaillé de toutes les fonctionnalités
              </h2>
            </div>
            <span className="text-xs text-slate-300 bg-white/10 px-3 py-1.5 rounded-xl border border-white/10">
              Devise : Franc CFA (FCFA)
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr>
                  <th className="p-4 bg-slate-950 text-white text-xs font-bold uppercase tracking-wider min-w-[280px]">
                    Fonctionnalités
                  </th>
                  <th className="p-4 bg-slate-800 text-white text-center text-xs font-bold min-w-[150px]">
                    <div>Découverte</div>
                    <div className="text-[11px] font-normal text-slate-300">Offre Gratuite</div>
                  </th>
                  <th className="p-4 bg-[#2f5c2f] text-white text-center text-xs font-bold min-w-[160px]">
                    <div>Initiale</div>
                    <div className="text-[11px] font-normal text-emerald-200">6 Mois (Ex-Découverte)</div>
                  </th>
                  <th className="p-4 bg-[#c25e00] text-white text-center text-xs font-bold min-w-[160px]">
                    <div>Essentiel</div>
                    <div className="text-[11px] font-normal text-amber-100">12 Mois</div>
                  </th>
                  <th className="p-4 bg-[#26374a] text-white text-center text-xs font-bold min-w-[180px]">
                    <div>Professionnel</div>
                    <div className="text-[11px] font-normal text-blue-200">12 Mois</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {featuresList.map((feat, index) => {
                  const isEven = index % 2 === 0;
                  const rowBg = isEven ? 'bg-[#f5f1fb]' : 'bg-white';

                  return (
                    <tr key={feat.id} className={`border-b border-slate-200 transition-colors hover:bg-violet-50/50 ${rowBg}`}>
                      {/* Nom de la fonctionnalité */}
                      <td className="p-3.5 text-xs font-medium text-slate-800 border-r border-slate-200">
                        {feat.name}
                      </td>

                      {/* Colonne Découverte (Gratuit) */}
                      <td className="p-3.5 text-center border-r border-slate-200">
                        {feat.discovery.included ? (
                          <div className="inline-flex flex-col items-center">
                            <Check className="w-4 h-4 text-violet-800 stroke-[2.5]" />
                            {feat.discovery.label && (
                              <span className="text-[10px] font-bold text-violet-800 mt-0.5">
                                {feat.discovery.label}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-400 font-bold text-sm">—</span>
                        )}
                      </td>

                      {/* Colonne Initiale (6 Mois - 70 000 FCFA) */}
                      <td className="p-3.5 text-center border-r border-slate-200">
                        {feat.initiale.included ? (
                          <div className="inline-flex flex-col items-center">
                            <Check className="w-4 h-4 text-violet-800 stroke-[2.5]" />
                            {feat.initiale.label && (
                              <span className="text-[10px] font-bold text-emerald-800 mt-0.5">
                                {feat.initiale.label}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-400 font-bold text-sm">—</span>
                        )}
                      </td>

                      {/* Colonne Essentiel (12 Mois - 110 000 FCFA) */}
                      <td className="p-3.5 text-center border-r border-slate-200">
                        {feat.essentiel.included ? (
                          <div className="inline-flex flex-col items-center">
                            <Check className="w-4 h-4 text-violet-800 stroke-[2.5]" />
                            {feat.essentiel.label && (
                              <span className="text-[10px] font-bold text-amber-900 mt-0.5">
                                {feat.essentiel.label}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-400 font-bold text-sm">—</span>
                        )}
                      </td>

                      {/* Colonne Professionnel (12 Mois - 200 000 FCFA) */}
                      <td className="p-3.5 text-center">
                        {feat.professionnel.included ? (
                          <div className="inline-flex flex-col items-center">
                            <Check className="w-4 h-4 text-violet-800 stroke-[2.5]" />
                            {feat.professionnel.label && (
                              <span className="text-[10px] font-bold text-blue-900 mt-0.5">
                                {feat.professionnel.label}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-400 font-bold text-sm">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}

                {/* Ligne Tarifaire Récapitulative du tableau */}
                <tr className="font-bold border-t-2 border-slate-300">
                  <td className="p-4 bg-slate-900 text-white text-xs uppercase tracking-wider">
                    Tarif d'abonnement
                  </td>
                  <td className="p-4 bg-slate-800 text-white text-center text-sm">
                    0 FCFA
                    <span className="block text-[10px] font-normal text-slate-300">Gratuit</span>
                  </td>
                  <td className="p-4 bg-[#2f5c2f] text-white text-center text-sm font-extrabold">
                    70 000 <span className="text-[10px] font-normal">FCFA</span>
                    <span className="block text-[10px] font-normal text-emerald-200">Pour 6 Mois</span>
                  </td>
                  <td className="p-4 bg-[#c25e00] text-white text-center text-sm font-extrabold">
                    110 000 <span className="text-[10px] font-normal">FCFA</span>
                    <span className="block text-[10px] font-normal text-amber-100">Pour 12 Mois</span>
                  </td>
                  <td className="p-4 bg-[#26374a] text-white text-center text-sm font-extrabold">
                    200 000 <span className="text-[10px] font-normal">FCFA</span>
                    <span className="block text-[10px] font-normal text-blue-200">Pour 12 Mois</span>
                  </td>
                </tr>

                {/* Ligne de souscription */}
                <tr>
                  <td className="p-4 bg-white text-slate-500 text-xs italic">
                    Action recommandée
                  </td>
                  <td className="p-3 text-center bg-white border-r border-slate-200">
                    <button
                      type="button"
                      onClick={() => {
                        if (!token) window.location.href = '/register';
                      }}
                      className="px-3 py-1.5 rounded-lg bg-slate-800 text-white text-xs font-bold hover:bg-slate-700 transition-all cursor-pointer"
                    >
                      {token ? 'Formule active' : 'S\'inscrire'}
                    </button>
                  </td>
                  <td className="p-3 text-center bg-white border-r border-slate-200">
                    <a
                      href="mailto:contact@docarnservices.com?subject=Souscription Formule Initiale 6 Mois"
                      className="inline-block px-3 py-1.5 rounded-lg bg-emerald-700 text-white text-xs font-bold hover:bg-emerald-800 transition-all cursor-pointer"
                    >
                      Souscrire
                    </a>
                  </td>
                  <td className="p-3 text-center bg-white border-r border-slate-200">
                    <a
                      href="mailto:contact@docarnservices.com?subject=Souscription Formule Essentiel 12 Mois"
                      className="inline-block px-3 py-1.5 rounded-lg bg-amber-600 text-white text-xs font-bold hover:bg-amber-700 transition-all cursor-pointer"
                    >
                      Souscrire
                    </a>
                  </td>
                  <td className="p-3 text-center bg-white">
                    <a
                      href="mailto:contact@docarnservices.com?subject=Souscription Formule Professionnel 12 Mois"
                      className="inline-block px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-bold hover:bg-slate-800 transition-all cursor-pointer"
                    >
                      Souscrire
                    </a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Assistance & Souscription personnalisée */}
      <div className="bg-slate-50 border border-slate-200 rounded-3xl p-8 sm:p-10 flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="space-y-2 text-center md:text-left">
          <div className="inline-flex items-center gap-2 text-xs font-bold text-violet-800 uppercase tracking-wider">
            <PhoneCall className="w-4 h-4 text-violet-700" />
            Accompagnement & Modalités de Paiement
          </div>
          <h3 className="text-xl font-bold text-slate-900">
            Besoin d'activer votre formule par Orange Money, MTN MoMo ou Virement ?
          </h3>
          <p className="text-xs sm:text-sm text-slate-600 max-w-xl">
            Nos conseillers sont disponibles pour activer votre abonnement en temps réel ou établir une convention entreprise multi-comptes sur mesure.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <a
            href="mailto:contact@docarnservices.com"
            className="px-6 py-3 bg-violet-700 hover:bg-violet-800 text-white font-bold text-xs rounded-xl transition-all shadow-sm flex items-center gap-2 cursor-pointer"
          >
            <span>Contacter le service client</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>
    </div>
  );
};

export default FormulasView;
